import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Trash2, HelpCircle } from 'lucide-react';

const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showFaq, setShowFaq] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Add welcome message when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Welcome to Aura Support! 👋 I'm here to help you build your system. Ask me about training, nutrition, recovery, or anything else!"
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      const data = await response.json();
      
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response 
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I'm having trouble connecting. Please try again or email support@auraapp.fit" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! Ready to help you build your system."
    }]);
    setSessionId(null);
  };

  const quickQuestions = [
    "How do I log a meal?",
    "What is Aura Score?",
    "How do streaks work?",
    "How do I track water?"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => sendMessage(), 100);
  };

  return (
    <>
      {/* Chat Button - positioned above mobile nav */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed z-50 w-14 h-14 rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${isOpen ? 'scale-0' : 'scale-100'} bottom-24 right-4 lg:bottom-6 lg:right-6`}
        data-testid="support-chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window - full screen on mobile, floating on desktop */}
      <div
        className={`fixed z-50 bg-zinc-900 shadow-2xl border border-white/10 flex flex-col overflow-hidden transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'} 
        inset-0 lg:inset-auto lg:bottom-6 lg:right-6 lg:w-96 lg:h-[550px] lg:rounded-2xl`}
        data-testid="support-chat-window"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Aura Support</h3>
              <p className="text-xs text-white/70">AI-Powered Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFaq(!showFaq)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="FAQ"
            >
              <HelpCircle className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={clearChat}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Clear chat"
            >
              <Trash2 className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {showFaq ? (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-white/70 uppercase">Quick Questions</h4>
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setShowFaq(false);
                    setInputValue(q);
                  }}
                  className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-white/90 transition-colors"
                >
                  {q}
                </button>
              ))}
              <button
                onClick={() => setShowFaq(false)}
                className="w-full text-center p-2 text-red-400 text-sm hover:underline"
              >
                Back to chat
              </button>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-red-500 text-white rounded-br-md'
                        : 'bg-white/10 text-white/90 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/10 p-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 text-white/70 animate-spin" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Quick Suggestions (only show when few messages) */}
        {messages.length <= 2 && !showFaq && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {quickQuestions.slice(0, 2).map((q, i) => (
              <button
                key={i}
                onClick={() => handleQuickQuestion(q)}
                className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-white/70 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-red-500/50 transition-colors"
              disabled={isLoading}
              data-testid="support-chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="px-4 py-3 bg-red-500 hover:bg-red-600 disabled:bg-white/10 disabled:cursor-not-allowed rounded-xl transition-colors"
              data-testid="support-chat-send"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
          <p className="text-xs text-white/40 text-center mt-2">
            AI-powered support • <a href="mailto:support@auraapp.fit" className="text-red-400 hover:underline">Email us</a> for complex issues
          </p>
        </div>
      </div>
    </>
  );
};

export default SupportChatWidget;
